import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import type { FacetDimension } from "../../../utils/facetFilters";
import type { PbjUpcomingTurnoverRow } from "../../../utils/models";
import type { SortingState } from "../../../utils/multiColumnSorting";
import { getEmploymentTypeDisplayName } from "../../../utils/utilFunctions";
import { MIN_TURNOVER_THRESHOLD_DAYS, usePbjUpcomingTurnover } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { sectionFrameHeight } from "../pbjGridCells";
import { pbjSortOptions } from "../pbjGridSorting";

type Props = {
	/** View-only "within N days" filter — lifted to AuditTab so the tab badge shares the same query. */
	thresholdDays: number;
	onThresholdDaysChange: (days: number) => void;
};

/** Marker for "already back on the schedule in time to prevent the turnover". */
const SCHEDULED_SHIFT_MARK = "✓";
/**
 * Text stand-in for the amber warning icon, used ONLY in the cell's underlying string value — which is
 * what search matches and what the CSV export writes, neither of which can render an icon or a colour.
 * On screen the same state renders as <WarningAmberOutlined/>, matching the rest of the app.
 */
const LATE_SCHEDULED_SHIFT_MARK = "⚠";

const formatDate = (iso: string | null): string => {
	if (!iso) return "—";
	const dt = DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
};

/**
 * The soonest committed shift as "<mark> <date>", or "" when nothing is scheduled at all.
 *
 * The GLYPH carries the in-time/too-late distinction, not the colour: this same string is what search
 * matches on and what the CSV export writes, and neither of those can read a colour. The two row fields
 * are mutually exclusive by construction on the backend, so the order of these checks is not a priority
 * rule — at most one is ever set.
 */
export const scheduledShiftCellValue = (row: PbjUpcomingTurnoverRow): string => {
	if (row.scheduledShiftDate) return `${SCHEDULED_SHIFT_MARK} ${formatDate(row.scheduledShiftDate)}`;
	if (row.lateScheduledShiftDate) return `${LATE_SCHEDULED_SHIFT_MARK} ${formatDate(row.lateScheduledShiftDate)}`;
	return "";
};

/** The three mutually exclusive states of the scheduled-shift column. */
export type ScheduledShiftState = "beforeTurnover" | "afterTurnover" | "notScheduled";

/**
 * Which of the three states a row is in. Single-sources the mutual exclusivity the backend guarantees, so
 * the filter and any future consumer cannot drift into disagreeing about what "not scheduled" means.
 */
export const scheduledShiftStateOf = (row: PbjUpcomingTurnoverRow): ScheduledShiftState => {
	if (row.scheduledShiftDate) return "beforeTurnover";
	if (row.lateScheduledShiftDate) return "afterTurnover";
	return "notScheduled";
};

const MOST_URGENT_FIRST: SortingState<PbjUpcomingTurnoverRow>[] = [{ sortColumns: ["daysUntilTurnover"], sortOrder: "asc" }];

const SCHEDULED_SHIFT_STATES: ScheduledShiftState[] = ["beforeTurnover", "afterTurnover", "notScheduled"];
const isScheduledShiftState = (value: string): value is ScheduledShiftState => (SCHEDULED_SHIFT_STATES as string[]).includes(value);

export enum TurnoverFacet {
	EmploymentType = "employment_type",
	ScheduledShift = "scheduled_shift",
}

export const TURNOVER_DIMENSIONS: Record<TurnoverFacet, FacetDimension<PbjUpcomingTurnoverRow>> = {
	[TurnoverFacet.EmploymentType]: { id: TurnoverFacet.EmploymentType, getKeys: (r) => [r.employmentType] },
	[TurnoverFacet.ScheduledShift]: { id: TurnoverFacet.ScheduledShift, getKeys: (r) => [scheduledShiftStateOf(r)] },
};

const COLUMN_FACET: Readonly<Record<string, string>> = {
	employmentType: TurnoverFacet.EmploymentType,
	scheduledShiftDate: TurnoverFacet.ScheduledShift,
};

/**
 * Read-only Upcoming Turnover audit — employees who WILL turn over (per the nurse-inactivity
 * notification rule) if they don't work again, evaluated as-of-today. The threshold input is a
 * pure view filter (not persisted): it commits on blur/Enter, mirroring the Long Shifts toolbar
 * layout but without a Save button since nothing is written.
 */
export const UpcomingTurnoverTable: FC<Props> = ({ thresholdDays, onThresholdDaysChange }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjUpcomingTurnover(thresholdDays);
	const labels = useFacetLabels();

	// Local input string so typing doesn't refetch per keystroke; re-seeded when the applied value changes.
	const [thresholdInput, setThresholdInput] = useState(String(thresholdDays));
	useEffect(() => setThresholdInput(String(thresholdDays)), [thresholdDays]);

	const rows = useMemo(() => data ?? [], [data]);
	const facets = useMemo<FacetSpec<PbjUpcomingTurnoverRow>[]>(() => {
		const scheduledLabel: Record<ScheduledShiftState, string> = {
			beforeTurnover: t("pbj_model.audit.upcoming_turnover.filters.scheduled.before"),
			afterTurnover: t("pbj_model.audit.upcoming_turnover.filters.scheduled.after"),
			notScheduled: t("pbj_model.audit.upcoming_turnover.filters.scheduled.none"),
		};
		return [
			{
				dimension: TURNOVER_DIMENSIONS[TurnoverFacet.ScheduledShift],
				title: t("pbj_model.audit.upcoming_turnover.cols.has_schedule_shift"),
				getLabel: (key) => (isScheduledShiftState(key) ? scheduledLabel[key] : key),
				order: SCHEDULED_SHIFT_STATES,
			},
			{
				dimension: TURNOVER_DIMENSIONS[TurnoverFacet.EmploymentType],
				title: t("pbj_model.audit.upcoming_turnover.cols.employment_type"),
				getLabel: labels.employmentType,
			},
		];
	}, [t, labels]);
	const filters = useGridFacetFilters(rows, facets);
	const railHandoff = useFilterRailHandoff(filters.highlight, COLUMN_FACET, t("pbj_model.filter.in_rail"));

	const commitThreshold = () => {
		const parsed = Number(thresholdInput);
		if (Number.isInteger(parsed) && parsed >= MIN_TURNOVER_THRESHOLD_DAYS) {
			onThresholdDaysChange(parsed);
		} else {
			// Invalid (empty / 0 / negative / fractional) — revert to the applied value rather than fetch garbage.
			setThresholdInput(String(thresholdDays));
		}
	};

	const columns: GridColDef<PbjUpcomingTurnoverRow>[] = useMemo(
		() => [
			{
				field: "empId",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.id"),
				minWidth: 140,
				flex: 1,
				// null = no external_employee mapping in the department; rendered blank per spec.
				valueFormatter: (value: string | null) => value ?? "",
			},
			{ field: "name", headerName: t("pbj_model.audit.upcoming_turnover.cols.name"), minWidth: 180, flex: 1 },
			{
				field: "employmentType",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.employment_type"),
				minWidth: 130,
				flex: 1,
				// PerDiem renders "Agency" — the label admins know; agency staff are alerted here (DAT-572).
				valueGetter: (_value, row) => getEmploymentTypeDisplayName(row.employmentType),
			},
			{
				field: "lastShiftDate",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.last_shift"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value: string) => formatDate(value),
			},
			{
				field: "daysUntilTurnover",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.days_until"),
				minWidth: 160,
				flex: 1,
				type: "number",
				align: "left",
				headerAlign: "left",
			},
			{
				field: "turnoverDate",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.turnover_date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value: string) => formatDate(value),
			},
			{
				field: "scheduledShiftDate",
				headerName: t("pbj_model.audit.upcoming_turnover.cols.has_schedule_shift"),
				minWidth: 180,
				flex: 1,
				// One computed string keeps search/sort/export coherent without a custom comparator.
				valueGetter: (_value, row) => scheduledShiftCellValue(row),
				// A shift landing after the turnover is a different call to action from no shift at all — the
				// employee IS coming back, the fix is to pull that shift earlier — so it gets the app's
				// standard amber warning treatment rather than sharing the reassuring ✓. The date is
				// re-formatted here instead of reusing `value`, which already carries the text stand-in mark.
				renderCell: ({ row, value }) =>
					row.lateScheduledShiftDate ? (
						<Tooltip title={t("pbj_model.audit.upcoming_turnover.late_shift_tooltip")}>
							<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
								<WarningAmberOutlined sx={{ fontSize: 16 }} />
								{formatDate(row.lateScheduledShiftDate)}
							</Box>
						</Tooltip>
					) : (
						value
					),
			},
		],
		[t],
	);

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<PbjUpcomingTurnoverRow>([
				{ field: "empId", label: t("pbj_model.audit.upcoming_turnover.cols.id") },
				{ field: "name", label: t("pbj_model.audit.upcoming_turnover.cols.name") },
				{ field: "employmentType", label: t("pbj_model.audit.upcoming_turnover.cols.employment_type") },
				{ field: "lastShiftDate", label: t("pbj_model.audit.upcoming_turnover.cols.last_shift") },
				{ field: "daysUntilTurnover", label: t("pbj_model.audit.upcoming_turnover.cols.days_until") },
				{ field: "turnoverDate", label: t("pbj_model.audit.upcoming_turnover.cols.turnover_date") },
				{ field: "scheduledShiftDate", label: t("pbj_model.audit.upcoming_turnover.cols.has_schedule_shift") },
			]),
		[t],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.upcoming_turnover.section.title")}
			description={t("pbj_model.audit.upcoming_turnover.section.subtitle")}
			dataTestId="pbj-upcoming-turnover-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<FilterRailFrame
				flat
				height={sectionFrameHeight(rows.length)}
				dataTestId="pbj-upcoming-turnover-filters-frame"
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-upcoming-turnover-filters"
					/>
				}
			>
				<SystemDataGrid<PbjUpcomingTurnoverRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(r) => r.nurseId}
					// employmentType searches on the rendered label so typing "Agency" matches a PerDiem row.
					searchableColumns={["empId", "name", { field: "employmentType", getValue: (r) => getEmploymentTypeDisplayName(r.employmentType) }]}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					defaultSortState={MOST_URGENT_FIRST}
					exportOptions={{ fileName: t("pbj_model.audit.upcoming_turnover.section.title") }}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.upcoming_turnover.failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.audit.upcoming_turnover.empty")} dataTestId="pbj-upcoming-turnover-empty" />}
							dataTestId="pbj-upcoming-turnover-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-upcoming-turnover-table"
					trailingToolbarNodes={
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1, flexGrow: 1 }}>
							<Stack direction="row" spacing={1} sx={{ alignItems: "center", ml: "auto" }}>
								<Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
									{t("pbj_model.audit.upcoming_turnover.threshold_label")}
								</Typography>
								<SystemValidationTextField
									inputType="number"
									value={thresholdInput}
									onChange={(e) => setThresholdInput(e.target.value)}
									reserveHeightForHelperText={false}
									formControlSx={{ width: 96 }}
									onKeyDown={(e) => {
										if (e.key === "Enter") commitThreshold();
									}}
									injectedProps={{
										// onBlur must ride injectedProps (no dedicated prop); onKeyDown has one — injectedProps' would be overridden.
										onBlur: commitThreshold,
										slotProps: { htmlInput: { min: MIN_TURNOVER_THRESHOLD_DAYS, step: 1 } },
									}}
									dataTestId="pbj-upcoming-turnover-threshold-input"
								/>
							</Stack>
						</Stack>
					}
				/>
			</FilterRailFrame>
		</SettingsSection>
	);
};
